import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Worker is configured globally in index.tsx - do not override here
console.log(`📄 PdfViewer: Using global worker configuration: ${pdfjs.GlobalWorkerOptions.workerSrc}`);

export interface CitationHighlight {
  page: number;
  bbox: [number, number, number, number];
}

interface PdfViewerProps {
  fileUrl: string;
  highlight?: CitationHighlight;
  /** Page to scroll to when the PDF loads (no highlight overlay rendered). */
  initialPage?: number;
  onClose: () => void;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ fileUrl, highlight, initialPage, onClose }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null);
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const displayWidth = 1000; // Increased for better full-screen experience

  // Fetch PDF with authentication if it's a streaming endpoint
  useEffect(() => {
    console.log('📄 PdfViewer: Received fileUrl:', fileUrl);
    console.log('📄 PdfViewer: URL type:', fileUrl.startsWith('data:') ? 'base64 data URL' : fileUrl.startsWith('blob:') ? 'blob URL' : fileUrl.startsWith('http') ? 'HTTP URL' : 'other');
    
    const fetchPdf = async () => {
      if (fileUrl.includes('/api/documents/') && fileUrl.includes('/stream')) {
        try {
          setLoading(true);
          const response = await fetch(fileUrl, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.status}`);
          }
          
          const blob = await response.blob();
          const dataUrl = URL.createObjectURL(blob);
          setPdfData(dataUrl);
        } catch (err) {
          console.error('📄 Error fetching PDF:', err);
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        } finally {
          setLoading(false);
        }
      } else {
        // For regular URLs, use them directly
        setPdfData(fileUrl);
        setLoading(false);
      }
    };

    fetchPdf();

    // Cleanup
    return () => {
      if (pdfData && pdfData.startsWith('blob:')) {
        URL.revokeObjectURL(pdfData);
      }
    };
  }, [fileUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page: any) => {
    if (!pageSize) {
      console.log('📄 Page object details:', {
        originalWidth: page.originalWidth,
        originalHeight: page.originalHeight,
        width: page.width,
        height: page.height,
        view: page.view,
        pageInfo: page.pageInfo,
        ref: page.ref
      });
      
      const { width, height } = page.originalWidth ? 
        { width: page.originalWidth, height: page.originalHeight } :
        { width: 612, height: 792 }; // Default A4 dimensions
      setPageSize({ width, height });
      console.log('📄 PDF page size set to:', { width, height });
    }
  };

  // Debug highlight when it changes
  useEffect(() => {
    if (highlight) {
      console.log('🟡 Highlight received in PdfViewer:', highlight);
      console.log('📏 Page size:', pageSize);
    }
  }, [highlight, pageSize]);

  useEffect(() => {
    if (highlight && containerRef.current) {
      const pageWrapper = containerRef.current.querySelector(
        `[data-page-number="${highlight.page}"]`
      ) as HTMLElement | null;
      if (pageWrapper) {
        pageWrapper.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [highlight]);

  // Plain page navigation (no highlight overlay). Waits for numPages so the
  // target page wrapper is actually mounted before scrolling.
  useEffect(() => {
    if (!initialPage || !containerRef.current || numPages === 0) return;
    const page = Math.max(1, Math.min(initialPage, numPages));
    const pageWrapper = containerRef.current.querySelector(
      `[data-page-number="${page}"]`
    ) as HTMLElement | null;
    if (pageWrapper) {
      pageWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [initialPage, numPages]);

  const handleDownload = () => {
    if (pdfData) {
      const link = document.createElement('a');
      link.href = pdfData;
      link.download = 'document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ESC key support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col z-50">
      {/* Header bar with controls */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            PDF Viewer
          </h3>
          {numPages > 0 && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {numPages} page{numPages !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleDownload}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Download PDF"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-4-4m4 4l4-4M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            Download
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Close (ESC)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* PDF content area */}
      <div className="flex-1 bg-gray-100 dark:bg-gray-900 overflow-auto">
        <div className="p-4 flex flex-col items-center" ref={containerRef}>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <p className="text-red-600 dark:text-red-400">Error loading PDF: {error}</p>
            </div>
          ) : pdfData ? (
            <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess}>
            {Array.from(new Array(numPages), (_, i) => i + 1).map(pageNum => (
              <div
                key={`page_${pageNum}`}
                data-page-number={pageNum}
                style={{ position: 'relative', marginBottom: '1rem' }}
              >
                <Page 
                  pageNumber={pageNum} 
                  width={displayWidth} 
                  onLoadSuccess={onPageLoadSuccess}
                />
                {highlight && highlight.page === pageNum && pageSize && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: `${highlight.bbox[0] * (displayWidth / pageSize.width)}px`,
                      top: `${highlight.bbox[1] * (displayWidth / pageSize.width)}px`,
                      width: `${highlight.bbox[2] * (displayWidth / pageSize.width)}px`,
                      height: `${highlight.bbox[3] * (displayWidth / pageSize.width)}px`,
                      border: '4px solid #EF4444',
                      backgroundColor: 'rgba(255, 255, 0, 0.6)',
                      zIndex: 1000,
                      boxShadow: '0 0 10px rgba(255, 0, 0, 0.8)'
                    }}
                    ref={(el) => {
                      if (el) {
                        const scaleFactor = displayWidth / pageSize.width;
                        console.log('🎯 Highlight overlay rendered:', {
                          bbox: highlight.bbox,
                          pageSize,
                          scaleFactor,
                          displayWidth,
                          calculatedPosition: {
                            left: highlight.bbox[0] * scaleFactor,
                            top: highlight.bbox[1] * scaleFactor,
                            width: highlight.bbox[2] * scaleFactor,
                            height: highlight.bbox[3] * scaleFactor
                          },
                          elementPosition: {
                            left: el.style.left,
                            top: el.style.top,
                            width: el.style.width,
                            height: el.style.height
                          }
                        });
                      }
                    }}
                  />
                )}
                {highlight && highlight.page === pageNum && !pageSize && (
                  <div 
                    className="absolute pointer-events-none"
                    style={{ 
                      top: '10px', 
                      left: '10px', 
                      color: 'red', 
                      fontSize: '20px',
                      fontWeight: 'bold',
                      zIndex: 1000,
                      backgroundColor: 'white',
                      padding: '5px',
                      border: '2px solid red'
                    }}
                  >
                    ⚠️ PageSize not loaded, highlight pending
                  </div>
                )}
                {highlight && highlight.page === pageNum && (
                  <div 
                    className="absolute pointer-events-none"
                    style={{ 
                      top: '50px', 
                      left: '10px', 
                      color: 'blue', 
                      fontSize: '16px',
                      fontWeight: 'bold',
                      zIndex: 1000,
                      backgroundColor: 'white',
                      padding: '5px',
                      border: '2px solid blue'
                    }}
                  >
                    📍 Highlight active for page {highlight.page}
                  </div>
                )}
                {highlight && highlight.page === pageNum && !pageSize && (
                  <div style={{ position: 'absolute', top: 0, left: 0, color: 'red', zIndex: 20 }}>
                    ⚠️ PageSize not loaded yet
                  </div>
                )}
              </div>
            ))}
          </Document>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PdfViewer; 